import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

export default function NewTour() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const { id: existingTourId } = useParams();   // present when on /tours/:id/edit
  const isResume = Boolean(existingTourId);
  const [categories, setCategories] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [tourId, setTourId] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [facilityError, setFacilityError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    if (isResume) {
      // Resume mode: load the existing draft + its responses
      Promise.all([
        API.get('/tours/questions'),
        API.get('/admin/facilities?tourable=true'),
        API.get(`/tours/${existingTourId}`),
      ]).then(([qRes, fRes, tRes]) => {
        setCategories(qRes.data);
        setFacilities(fRes.data);

        const { tour, responses: existingResponses } = tRes.data;

        // Guard: only drafts are editable. If tour has been submitted, send to detail page.
        if (tour.status !== 'draft') {
          navigate(`/tours/${existingTourId}`, { replace: true });
          return;
        }

        setTourId(tour.id);
        setSelectedFacility(String(tour.facility_id));

        // Hydrate responses keyed by question_id
        const hydrated = {};
        existingResponses.forEach(r => {
          hydrated[r.question_id] = {
            value: r.response_value,
            label: r.response_label,
            notes: r.notes || '',
          };
        });
        setResponses(hydrated);
      }).catch(err => {
        setError('Failed to load draft. ' + (err.response?.data?.error || err.message));
      }).finally(() => setLoading(false));
      return;
    }

    // New-tour mode: load questions + facilities + current availability
    Promise.all([
      API.get('/tours/questions'),
      API.get('/admin/facilities?tourable=true'),
      API.get('/tours/facility-availability'),
    ]).then(([qRes, fRes, aRes]) => {
      setCategories(qRes.data);
      setFacilities(fRes.data);
      const avail = {};
      (aRes.data || []).forEach(a => { avail[a.facility_id] = a; });
      setAvailability(avail);
      // Pre-select assigned facility only for building_admin (they tour their own building).
      // Staff often have a corporate facility_id but tour many buildings, so we force
      // them to actively pick the correct facility every time.
      if (user?.role === 'building_admin' && user?.facilityId) {
        const a = avail[user.facilityId];
        const blocked = a && (a.open_draft || a.recent_submitted);
        if (!blocked) setSelectedFacility(String(user.facilityId));
      }
    }).finally(() => setLoading(false));
  }, [isResume, existingTourId]);

  // Availability map: facility_id -> { open_draft, recent_submitted }
  const [availability, setAvailability] = useState({});
  const canOverrideLimit = ['super_admin', 'regional_manager'].includes(user?.role);
  const facilityBlockReason = (fid) => {
    const a = availability[fid];
    if (!a) return null;
    if (a.open_draft) {
      const d = a.open_draft;
      return `active tour in progress (started by ${d.by_name} ${format(new Date(d.created_at), 'MMM d')})`;
    }
    if (a.recent_submitted) {
      const s = a.recent_submitted;
      const next = new Date(new Date(s.submitted_at).getTime() + 7 * 86400000);
      return `toured this week — next tour ${format(next, 'MMM d')}`;
    }
    return null;
  };

  // Start tour when facility selected (only in new-tour mode — resume already has a tourId)
  const [createError, setCreateError] = useState('');
  useEffect(() => {
    if (!isResume && selectedFacility && !tourId) {
      // Override-capable roles picking a blocked facility must confirm first —
      // the draft is only created after an explicit yes.
      const reason = facilityBlockReason(selectedFacility);
      if (reason && canOverrideLimit) {
        const ok = window.confirm(`This facility is ${reason}.\n\nStart another tour anyway?`);
        if (!ok) { setSelectedFacility(''); return; }
      }
      setCreateError('');
      API.post('/tours', { facilityId: selectedFacility })
        .then(r => setTourId(r.data.id))
        .catch(e => {
          // 409 = weekly tour limit; anything else = generic failure.
          setCreateError(e.response?.data?.error || 'Could not start the tour. Please try again.');
          setSelectedFacility('');
        });
    }
  }, [selectedFacility, isResume]);

  const totalQuestions = categories.reduce((sum, c) => sum + c.questions.length, 0);
  const answeredCount = Object.keys(responses).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const handleResponse = (questionId, value, label) => {
    setResponses(prev => ({ ...prev, [questionId]: { ...prev[questionId], value, label } }));
  };

  const handleNotes = (questionId, notes) => {
    setResponses(prev => ({ ...prev, [questionId]: { ...prev[questionId], notes } }));
  };

  const autoSave = useCallback(async () => {
    if (!tourId || Object.keys(responses).length === 0) return;
    setSaving(true);
    try {
      const payload = Object.entries(responses).map(([questionId, r]) => ({
        questionId, responseValue: r.value, responseLabel: r.label, notes: r.notes || ''
      }));
      await API.put(`/tours/${tourId}/responses`, { responses: payload });
      setSaveStatus('Saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (e) {
      setSaveStatus('Save failed');
    } finally {
      setSaving(false);
    }
  }, [tourId, responses]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const timer = setInterval(autoSave, 30000);
    return () => clearInterval(timer);
  }, [autoSave]);

  // Show facility selector for all roles. Staff & building_admin get pre-selected
  // to their assigned facility but can still change it.
  const showFacilityDropdown = facilities.length > 0;

  const handleSubmit = async () => {
    // Mandatory facility validation
    if (showFacilityDropdown && !selectedFacility) {
      setFacilityError('Please select a facility before submitting.');
      // Scroll the facility selector into view so the error is visible
      const sel = document.getElementById('facility-select');
      if (sel) {
        sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sel.focus();
      }
      return;
    }

    if (answeredCount < totalQuestions) {
      const unanswered = totalQuestions - answeredCount;
      if (!window.confirm(`${unanswered} question(s) are unanswered. Submit anyway?`)) return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = Object.entries(responses).map(([questionId, r]) => ({
        questionId, responseValue: r.value, responseLabel: r.label, notes: r.notes || ''
      }));
      if (payload.length > 0) {
        await API.put(`/tours/${tourId}/responses`, { responses: payload });
      }
      const result = await API.post(`/tours/${tourId}/submit`);
      navigate(`/tours/${tourId}`, {
        state: { justSubmitted: true, score: result.data.complianceScore, issueCount: result.data.issueCount }
      });
    } catch (e) {
      setError('Failed to submit tour. Please try again.');
      setSubmitting(false);
    }
  };

  const getCategoryCompliance = (cat) => {
    const answered = cat.questions.filter(q => responses[q.id]?.value !== undefined);
    // N/A counts as compliant (any answered value other than '0')
    const compliant = answered.filter(q => responses[q.id]?.value !== '0');
    return { answered: answered.length, total: cat.questions.length, compliant: compliant.length };
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;

  // Submit is disabled until a tour exists (which only happens after facility selection)
  // OR until facility is selected for non-staff roles. This is belt-and-suspenders alongside
  // the explicit validation in handleSubmit.
  const submitDisabled = submitting || !tourId || (showFacilityDropdown && !selectedFacility);

  return (
    <>
      <div className="page-header">
        <div>
          <h2>{isResume ? 'Resume Tour' : 'New Facility Tour'}</h2>
          <p>{answeredCount} of {totalQuestions} questions answered</p>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center'}}>
          {saveStatus && <span style={{fontSize:13,color:'var(--text-secondary)'}}>{saving ? '⏳' : '✓'} {saveStatus}</span>}
          <button className="btn btn-secondary" onClick={autoSave} disabled={saving}>Save Draft</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitDisabled}>
            {submitting ? 'Submitting...' : 'Submit Tour'}
          </button>
        </div>
      </div>

      <div className="page-body">
        {error && <div className="alert-banner alert-danger">{error}</div>}

        {/* Progress bar */}
        <div className="card" style={{marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontSize:13,fontWeight:600,color:'var(--gray-700)'}}>Tour Progress</span>
            <span style={{fontSize:13,color:'var(--text-secondary)'}}>{Math.round(progress)}% complete</span>
          </div>
          <div className="progress-wrap">
            <div className="progress-bar" style={{width:`${progress}%`}}/>
          </div>
        </div>

        {/* Facility selector */}
        {showFacilityDropdown && (
          <div className="card" style={{marginBottom:20}}>
            {createError && (
              <div className="alert-banner alert-danger" style={{marginBottom:12}}>{createError}</div>
            )}
            {canOverrideLimit && selectedFacility && facilityBlockReason(selectedFacility) && (
              <div className="alert-banner" style={{marginBottom:12, background:'rgba(245,158,11,0.12)', border:'1px solid var(--amber)', color:'var(--amber)'}}>
                Heads up: this facility is {facilityBlockReason(selectedFacility)}. Your role can override this limit and proceed anyway.
              </div>
            )}
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label" htmlFor="facility-select">
                Select Facility <span style={{color:'var(--red)'}}>*</span>
              </label>
              <select
                id="facility-select"
                className={`form-control ${facilityError ? 'is-invalid' : ''}`}
                value={selectedFacility}
                onChange={async e => {
                  const newVal = e.target.value;
                  const previousVal = selectedFacility;
                  setSelectedFacility(newVal);
                  if (newVal) setFacilityError('');

                  // If a tour already exists (either resumed or created by the
                  // initial facility selection in new-tour mode), sync the change
                  // to the server. Without this, the dropdown updates locally but
                  // the underlying tour stays bound to the original facility,
                  // causing submitted tours to show the wrong facility name.
                  if (tourId && newVal) {
                    try {
                      await API.put(`/tours/${tourId}/facility`, { facilityId: newVal });
                    } catch (err) {
                      setFacilityError('Failed to update facility: ' + (err.response?.data?.error || err.message));
                      setSelectedFacility(previousVal);   // revert local UI on failure
                    }
                  }
                }}
              >
                <option value="">-- Select Facility --</option>
                {facilities.map(f => {
                  const reason = facilityBlockReason(f.id);
                  return (
                    <option key={f.id} value={f.id} disabled={!!reason && !canOverrideLimit}>
                      {f.name}{reason ? ` — ⛔ ${reason}` : ''}
                    </option>
                  );
                })}
              </select>
              {facilityError && <div className="field-error">{facilityError}</div>}
            </div>
          </div>
        )}

        <div className="tour-form">
          {categories.map(category => {
            const { answered, total, compliant } = getCategoryCompliance(category);
            return (
              <div key={category.id} className="tour-category">
                <div className="tour-category-header">
                  <span>{category.name}</span>
                  <span className="category-progress">
                    {answered}/{total} answered
                    {answered > 0 && ` · ${compliant} compliant`}
                  </span>
                </div>
                {category.questions.map(q => {
                  const resp = responses[q.id];
                  // N/A (any answered value other than '0') styles as a
                  // positive answer — only true non-compliance shows red.
                  const selectClass = resp?.value === '0'
                    ? 'question-select selected-noncompliant'
                    : resp?.value
                    ? 'question-select selected-compliant'
                    : 'question-select';

                  return (
                    <div key={q.id} className="tour-question">
                      <div className="question-text-wrap">
                        <span className="question-number">{q.number}</span>
                        <span className="question-label">{q.text}</span>
                      </div>
                      <div className="question-right">
                        <select
                          className={selectClass}
                          value={resp?.value ?? ''}
                          onChange={e => {
                            handleResponse(q.id, e.target.value, e.target.options[e.target.selectedIndex].text);
                          }}
                        >
                          <option value="">-- Select --</option>
                          {(q.dropdownOptions || ['1 - Compliant', '0 - Not Compliant']).map((opt, i) => {
                            const label = typeof opt === 'string' ? opt : opt.label;
                            const value = typeof opt === 'string'
                              ? (label.startsWith('1') ? '1' : label.startsWith('0') ? '0' : String(i))
                              : opt.value;
                            return <option key={i} value={value}>{label}</option>;
                          })}
                        </select>
                        <textarea
                          className="notes-input"
                          placeholder="Notes (optional)..."
                          value={resp?.notes || ''}
                          onChange={e => handleNotes(q.id, e.target.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:24,paddingBottom:32}}>
            <button className="btn btn-secondary" onClick={() => navigate('/tours')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitDisabled}>
              {submitting ? 'Submitting...' : '✓ Submit Tour'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
